'use client';
import { forwardRef, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalytics } from '../../Analytics';
import { useAnalyticsPageView } from '../../Analytics/AnalyticsPageView';

export type FeatureViewedProps = {
  additionalAttributes?: object;
  contextCampaignUtmCampaign?: string;
  entityName: string;
  entitySection?: string;
  impressionPosition?: number;
  impressionSection?: string;
  children: React.JSX.Element;
};

export const FeatureViewed = forwardRef<HTMLElement, FeatureViewedProps>(
  (
    {
      additionalAttributes,
      contextCampaignUtmCampaign,
      entityName,
      entitySection,
      impressionPosition,
      impressionSection,
      children,
    },
    ref,
  ) => {
    const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
    const { pageViewId, pageName, pageViewReady, pageType } = useAnalyticsPageView();
    const { ref: inViewRef, inView } = useInView({
      root: null,
      threshold: 0,
      rootMargin: '1px 0px 0px 0px',
      triggerOnce: true,
    });

    const segmentInstance = analytics?.segmentInstance;

    // display: contents makes this anchor invisible to layout (its child keeps the parent's
    // flex/grid context). We read the real child node off the anchor's ref instead of injecting
    // a ref via cloneElement, which breaks when `children` crosses a server/client boundary.
    // `children` is a dep so a changed child forces React to re-run this ref against fresh DOM.
    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        const target = (node?.firstElementChild as HTMLElement | null) ?? null;
        inViewRef(target);
        if (typeof ref === 'function') {
          ref(target);
        } else if (ref) {
          ref.current = target;
        }
      },
      [inViewRef, ref, children],
    );

    useEffect(() => {
      if (pageViewReady && deviceId && inView && segmentInstance) {
        segmentInstance.track(
          'Feature Viewed',
          {
            entity_name: entityName,
            entity_section: entitySection,
            impression_page_url: window?.location?.href,
            impression_pageview_id: pageViewId,
            ...(impressionPosition !== null && {
              impression_position: impressionPosition,
            }),
            ...(impressionSection && { impression_section: impressionSection }),
            impression_page_name: pageName || window.location.pathname,
            deployable_name: deployableName,
            deployable_version: deployableVersion,
            traditional_device_id: deviceId,
            ...(contextCampaignUtmCampaign && {
              context_campaign_utm_campaign: contextCampaignUtmCampaign,
            }),
            additional_attributes: {
              ...additionalAttributes,
              page_type: pageType,
            },
          },
          {
            context: {
              app: {
                name: deployableName,
                version: gitSha,
              },
            },
          },
        );
      }
    }, [pageViewReady, deviceId, inView, impressionPosition]);

    // Removed cloneElement to avoid breaking server/client boundaries on dev.
    // Instead, we use a div with display: contents to wrap the children and set the ref on that div. This allows us to keep the layout intact while still tracking the feature view event.
    // Restore cloneElement if issue with dev server/client boundaries is resolved in the future.
    return (
      <div ref={setRefs} style={{ display: 'contents' }}>
        {children}
      </div>
    );
  },
);
